import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, ArrowUp, ArrowDown, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const DEFAULT_CATEGORIES = [
  "visa_status", "work_preference", "marketing_type", "placement_field",
  "technologies", "roles", "document_category", "gender", "marital_status",
  "salary_currency", "source_of_hire", "candidate_status", "hold_reason",
  "evaluation_platform",
];

function humanize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function MasterDataAdmin() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [category, setCategory] = useState("visa_status");
  const [rows, setRows] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [showInactive, setShowInactive] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadCats = useCallback(async () => {
    try {
      const { data } = await api.get("/master-data/categories");
      const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...data])).sort();
      setCategories(merged);
    } catch (e) {
      // fall back to defaults
    }
  }, []);

  const loadRows = useCallback(async () => {
    if (!category) return;
    setBusy(true);
    try {
      const { data } = await api.get("/master-data", {
        params: { category, include_inactive: showInactive },
      });
      setRows(data);
    } finally {
      setBusy(false);
    }
  }, [category, showInactive]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const addValue = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) return;
    try {
      await api.post("/master-data", {
        category, value: newValue.trim(), sort_order: rows.length,
      });
      toast.success("Value added");
      setNewValue("");
      loadRows();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/master-data/${row.id}`, { active: !row.active });
      loadRows();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const rename = async (row, value) => {
    if (!value || value === row.value) return;
    try {
      await api.put(`/master-data/${row.id}`, { value });
      loadRows();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
    try {
      await api.post("/master-data/reorder", {
        category, ordered_ids: next.map((r) => r.id),
      });
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); loadRows(); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Deactivate "${row.value}"? Existing records will keep this value.`)) return;
    try {
      await api.delete(`/master-data/${row.id}`);
      loadRows();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="master-data-admin">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-slate-500">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="md-category-select"
            className="border border-slate-200 rounded-md px-3 py-2 text-sm min-w-[200px]"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{humanize(c)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">New category</label>
          <input
            placeholder="add new category…"
            data-testid="md-new-category"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                const v = e.target.value.trim().toLowerCase().replace(/\s+/g, "_");
                setCategories((prev) => Array.from(new Set([...prev, v])).sort());
                setCategory(v);
                e.target.value = "";
              }
            }}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="md-show-inactive"
          />
          Show inactive
        </label>
      </div>

      <form onSubmit={addValue} className="flex gap-2 items-center">
        <input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={`Add value to "${humanize(category)}"…`}
          data-testid="md-new-value"
          className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
        />
        <button
          type="submit"
          data-testid="md-add-btn"
          className="bg-sky-600 hover:bg-sky-700 text-white text-sm rounded-md px-4 py-2 inline-flex items-center gap-1"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-2">Order</th>
              <th className="text-left px-4 py-2">Value</th>
              <th className="text-left px-4 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={4} className="text-center text-slate-400 py-6">Loading…</td></tr>}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={4} className="text-center text-slate-400 py-6">No values in this category.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-2 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} className="p-1 hover:bg-slate-100 rounded" data-testid={`md-up-${r.id}`}>
                      <ArrowUp size={12} />
                    </button>
                    <button onClick={() => move(i, +1)} className="p-1 hover:bg-slate-100 rounded" data-testid={`md-down-${r.id}`}>
                      <ArrowDown size={12} />
                    </button>
                    <span className="ml-1">{i + 1}</span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <input
                    defaultValue={r.value}
                    onBlur={(e) => rename(r, e.target.value.trim())}
                    data-testid={`md-value-${r.id}`}
                    className={`w-full border-0 focus:border focus:border-slate-300 focus:outline-none rounded px-2 py-1 ${r.active ? "" : "text-slate-400 line-through"}`}
                  />
                </td>
                <td className="px-4 py-2">
                  <button
                    onClick={() => toggleActive(r)}
                    data-testid={`md-toggle-${r.id}`}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                  >
                    {r.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {r.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => remove(r)} className="text-red-600 hover:text-red-800 p-1" data-testid={`md-remove-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
